'use client';

import { useState, type ReactNode } from 'react';
import { Box, HStack, VStack, Divider } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { handleButtonKeyDown } from './handleButtonKeyDown';
import { IndicatorDot } from './IndicatorDot';

type SidebarSectionProps = {
  label: string;
  children: ReactNode;
  headerAction?: ReactNode;
  hasActiveChild?: boolean;
  collapsed?: boolean;
  keepHeaderActionVisible?: boolean;
};

export function SidebarSection({
  label,
  children,
  headerAction,
  hasActiveChild,
  collapsed,
  keepHeaderActionVisible,
}: SidebarSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  if (collapsed) {
    return (
      <VStack gap="0" w="full" pt="4" alignItems="stretch">
        <Divider width="60%" alignSelf="center" my="1" />
        {children}
      </VStack>
    );
  }

  return (
    <VStack
      gap="0"
      w="full"
      pt="4"
      alignItems="stretch"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <HStack gap="1" alignItems="center" h="7" px="2" pt="1" pb="2">
        <HStack
          role="button"
          tabIndex={0}
          gap="1"
          alignItems="center"
          cursor="pointer"
          onClick={() => setIsCollapsed((prev) => !prev)}
          onKeyDown={handleButtonKeyDown}
        >
          <MetaDefault color="fg.neutral.muted" whiteSpace="nowrap">
            {label}
          </MetaDefault>
          <Box
            color="fg.neutral.muted"
            display="flex"
            alignItems="center"
            flexShrink={0}
          >
            {isCollapsed ? (
              <ArrowRight01Icon size={12} />
            ) : (
              <ArrowDown01Icon size={12} />
            )}
          </Box>
          {isCollapsed && hasActiveChild && <IndicatorDot position="inline" />}
        </HStack>
        <Box flex="1" minW="0" />
        {headerAction && (
          <Box
            opacity={isHovered || keepHeaderActionVisible ? 1 : 0}
            visibility={
              isHovered || keepHeaderActionVisible ? 'visible' : 'hidden'
            }
            pointerEvents={
              isHovered || keepHeaderActionVisible ? 'auto' : 'none'
            }
            transition="opacity 150ms ease"
            display="flex"
            alignItems="center"
            flexShrink={0}
          >
            {headerAction}
          </Box>
        )}
      </HStack>

      {!isCollapsed && (
        <VStack gap="0" w="full" alignItems="stretch">
          {children}
        </VStack>
      )}
    </VStack>
  );
}
